import React from 'react';
import { Edit2, XCircle, X } from 'lucide-react';
import { useAdmin } from '../context/AdminContext';

export default function StopsPage() {
  const {
    stops, loadingStops, showStopModal, setShowStopModal,
    selectedStop, setSelectedStop, stopForm, setStopForm, loadStopsFromAPI,
  } = useAdmin();

  const handleAddStop = () => { setStopForm({ name: '', description: '' }); setSelectedStop(null); setShowStopModal(true); };
  const handleEditStop = (stop) => { setStopForm({ name: stop.name, description: stop.description || '' }); setSelectedStop(stop.stop_id); setShowStopModal(true); };

  const handleSaveStop = async () => {
    if (!stopForm.name.trim()) { alert("Le nom de l'arrêt est requis"); return; }
    try {
      const apiService = (await import('../apiService')).default;
      if (selectedStop) {
        await apiService.put(`/stops/${selectedStop}`, stopForm, { userType: 'admin' });
      } else {
        await apiService.post('/stops', stopForm, { userType: 'admin' });
      }
      setShowStopModal(false);
      loadStopsFromAPI();
    } catch (error) { alert(error.message || 'Erreur lors de la sauvegarde'); }
  };

  const handleDeleteStop = async (stopId) => {
    if (!window.confirm('Supprimer cet arrêt ?')) return;
    try {
      const apiService = (await import('../apiService')).default;
      await apiService.delete(`/stops/${stopId}`, { userType: 'admin' });
      loadStopsFromAPI();
    } catch (error) { alert(error.message || 'Impossible de supprimer cet arrêt'); }
  };

  const handleToggleStopStatus = async (stop) => {
    try {
      const apiService = (await import('../apiService')).default;
      await apiService.put(`/stops/${stop.stop_id}`, { active: !stop.active }, { userType: 'admin' });
      loadStopsFromAPI();
    } catch (error) { alert(error.message || 'Erreur'); }
  };

  return (
    <div className="dashboard-content">
      <div className="page-header">
        <h1 className="page-title">Gestion des Arrêts</h1>
        <button className="btn-primary" onClick={handleAddStop} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>+ Nouvel Arrêt</button>
      </div>
      <p style={{ color: '#5C636E', marginBottom: '1.5rem', fontSize: '0.95rem' }}>
        Les arrêts sont des points centraux (ex: Nyawera, Place du 24, Kadutu). Les lignes sont créées en combinant deux arrêts.
      </p>

      {showStopModal && (
        <div className="modal-overlay" onClick={() => setShowStopModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ width: '500px', maxWidth: '90vw' }}>
            <div className="modal-header">
              <h2>{selectedStop ? "Modifier l'arrêt" : 'Nouvel arrêt'}</h2>
              <button className="close-btn" onClick={() => setShowStopModal(false)}><X size={24} /></button>
            </div>
            <div className="modal-body" style={{ padding: '2rem' }}>
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#1E2229' }}>Nom de l'arrêt *</label>
                <input autoFocus type="text" value={stopForm.name} onChange={(e) => setStopForm({ ...stopForm, name: e.target.value })}
                  onKeyDown={(e) => e.stopPropagation()} placeholder="ex: Nyawera, Place du 24, Kadutu..." spellCheck="false"
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #E9EBEF', borderRadius: '6px', fontSize: '1rem', boxSizing: 'border-box' }} />
              </div>
              <div style={{ marginBottom: '2rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#1E2229' }}>Description (optionnel)</label>
                <input type="text" value={stopForm.description} onChange={(e) => setStopForm({ ...stopForm, description: e.target.value })}
                  onKeyDown={(e) => e.stopPropagation()} placeholder="ex: Arrêt principal du marché" spellCheck="false"
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #E9EBEF', borderRadius: '6px', fontSize: '1rem', boxSizing: 'border-box' }} />
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <button className="btn-secondary" onClick={() => setShowStopModal(false)} style={{ padding: '0.75rem 1.5rem' }}>Annuler</button>
                <button className="btn-primary" onClick={handleSaveStop} style={{ padding: '0.75rem 1.5rem' }}>{selectedStop ? 'Modifier' : 'Créer'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {loadingStops ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#5C636E' }}>Chargement...</div>
      ) : stops.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#5C636E' }}>
          <p style={{ fontSize: '1.1rem' }}>Aucun arrêt défini</p>
          <button className="btn-primary" onClick={handleAddStop} style={{ marginTop: '1rem' }}>+ Créer un premier arrêt</button>
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr><th>Arrêt</th><th>Description</th><th>Statut</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {stops.map(stop => (
                <tr key={stop.stop_id}>
                  <td style={{ fontWeight: '600' }}>{stop.name}</td>
                  <td style={{ color: '#5C636E' }}>{stop.description || '—'}</td>
                  <td>
                    <span style={{ padding: '0.35rem 0.75rem', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '600', background: stop.active ? '#ECFDF5' : '#fee2e2', color: stop.active ? '#166534' : '#991b1b', textTransform: 'uppercase', cursor: 'pointer' }}
                      onClick={() => handleToggleStopStatus(stop)}>
                      {stop.active ? '✅ Actif' : '❌ Inactif'}
                    </span>
                  </td>
                  <td style={{ display: 'flex', gap: '0.5rem' }}>
                    <button className="icon-btn" onClick={() => handleEditStop(stop)} title="Modifier" style={{ padding: '0.5rem', color: '#FF7800' }}><Edit2 size={18} /></button>
                    <button className="icon-btn" onClick={() => handleDeleteStop(stop.stop_id)} title="Supprimer" style={{ padding: '0.5rem', color: '#DC2626' }}><XCircle size={18} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
